import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/ds/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { CheckIcon, DownloadSimpleIcon, XIcon } from "@phosphor-icons/react";
import { ChevronDown } from "lucide-react";
import { exportProject, type TExportProjectOptions } from "../lib/pdf-export";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { usePresentationStore } from "@/zapi-frontend/stores/presentation";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { ExportOptionsModal } from "./_ExportOptionsModal";
import $api from "@/zapi-frontend/shared/api/axios";
import { useQueryClient } from "@tanstack/react-query";
import { useEditorStore } from "@/zapi-frontend/stores/editor";

const PRESENTATION_FORMATS = ["pdf", "pptx"] as const;
type TPresentationFormat = (typeof PRESENTATION_FORMATS)[number];

const sanitizeFileName = (value: string) => value.replace(/[^\p{L}\p{N}_\-\s]/gu, "");

export function ExportButton({ disabled = false }: { disabled?: boolean }) {
  const { t } = useTranslation(["projectHome", "common"]);
  const [searchParams] = useSearchParams();
  const isPresentationTab = searchParams.get("tab") === "presentation";
  const queryClient = useQueryClient();

  const { selectedProject, isFetchProjectLoading } = useProjectStore();
  const { presentation } = usePresentationStore();
  const { flushSave } = useEditorStore();
  const [isExporting, setIsExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [presentationModalOpen, setPresentationModalOpen] = useState(false);
  const [presentationFileName, setPresentationFileName] = useState("");
  const [presentationFileType, setPresentationFileType] = useState<TPresentationFormat>("pdf");

  const presentationFileUrl = (format: TPresentationFormat) =>
    format === "pdf" ? presentation?.gammaPdfUrl : presentation?.gammaPptxUrl;
  const availablePresentationFormats = PRESENTATION_FORMATS.filter((format) => presentationFileUrl(format));

  const handleExport = async (options: TExportProjectOptions) => {
    setModalOpen(false);
    setIsExporting(true);
    const toastId = toast.loading(t("common:exporting"));
    try {
      await flushSave?.();
      await exportProject(selectedProject?.id as unknown as number, options);

      await queryClient.invalidateQueries({
        queryKey: ["exportHistory", selectedProject?.id.toString()]
      });

      toast.success(t("header.exportDocSuccess"), { id: toastId });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("header.exportDocFailed"), { id: toastId });
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const openPresentationModal = () => {
    setPresentationFileName(sanitizeFileName(selectedProject?.name || "presentation").trim());
    setPresentationFileType(availablePresentationFormats[0] ?? "pdf");
    setPresentationModalOpen(true);
  };

  const handlePresentationExport = async () => {
    if (!selectedProject?.id) return;

    const sourceUrl = presentationFileUrl(presentationFileType);
    if (!sourceUrl) {
      toast.error(t("header.exportFormatUnavailable", { format: presentationFileType.toUpperCase() }));
      return;
    }

    try {
      setIsExporting(true);
      const finalName = presentationFileName.trim() || "presentation";
      const response = await $api.get<{ data: { url: string } }>(
        `/projects/${selectedProject.id}/presentations/download-url`,
        { params: { fileType: presentationFileType, fileName: finalName } }
      );

      const signedUrl = response.data.data.url;
      const fileResponse = await fetch(signedUrl);
      const blob = await fileResponse.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${finalName}.${presentationFileType}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      setPresentationModalOpen(false);

      await queryClient.invalidateQueries({
        queryKey: ["exportHistory", selectedProject.id.toString()]
      });
    } catch (error) {
      console.error("Download error:", error);
      toast.error(t("header.exportDownloadFailed"));
    } finally {
      setIsExporting(false);
    }
  };

  const hasPresentationFiles = availablePresentationFormats.length > 0;
  const isPresentationExportDisabled = disabled || !hasPresentationFiles || isFetchProjectLoading || isExporting;
  const isDocumentExportDisabled = disabled || isExporting || isFetchProjectLoading;

  if (isPresentationTab) {
    return (
      <>
        <Button
          type="button"
          variant="secondary"
          title={t("header.exportPresentation")}
          disabled={isPresentationExportDisabled}
          onClick={openPresentationModal}
          data-testid="export-menu-trigger"
        >
          <DownloadSimpleIcon className="size-4" />
          <FigmaTypography variant="label-2" color={isPresentationExportDisabled ? "zinc400" : "zinc700"}>
            {t("common:exportAs")}
          </FigmaTypography>
        </Button>

        <Dialog open={presentationModalOpen} onOpenChange={(open) => !isExporting && setPresentationModalOpen(open)}>
          <DialogContent className="sm:max-w-[425px] gap-5 p-5" showCloseButton={false} closeLabel={t("common:close")}>
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-medium">{t("common:export")}</DialogTitle>
                <DialogClose
                  data-testid="presentation-export-close-button"
                  className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none"
                  disabled={isExporting}
                >
                  <XIcon className="size-5" />
                  <span className="sr-only">Close</span>
                </DialogClose>
              </div>
            </DialogHeader>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="presentation-filename">
                <FigmaTypography variant="body-2" color="zinc900" className="leading-4">
                  {t("common:fileName")}
                </FigmaTypography>
              </label>
              <div className="flex h-9 items-stretch overflow-hidden rounded-lg border border-innoveraZinc-150 focus-within:border-innoveraZinc-300">
                <div className="relative flex flex-1 flex-col gap-2">
                  <Input
                    id="presentation-filename"
                    className="h-full flex-1 rounded-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    value={presentationFileName}
                    onChange={(e) => setPresentationFileName(sanitizeFileName(e.target.value))}
                    placeholder="presentation"
                    disabled={isExporting}
                    data-testid="presentation-filename-input"
                  />
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="presentation-format-trigger"
                    disabled={isExporting}
                    render={
                      <button
                        type="button"
                        className="flex items-center gap-1 border-l border-innoveraZinc-150 bg-innoveraZinc-50 px-3"
                      />
                    }
                  >
                    <span className="text-sm font-normal text-innoveraZinc-900">.{presentationFileType}</span>
                    <ChevronDown className="size-4 text-innoveraZinc-900" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={8} className="w-auto min-w-48 rounded-xl p-1.5">
                    {availablePresentationFormats.map((type) => (
                      <DropdownMenuItem
                        key={type}
                        data-testid={`presentation-format-${type}`}
                        onClick={() => setPresentationFileType(type)}
                      >
                        <CheckIcon size={14} className={presentationFileType === type ? "opacity-100" : "opacity-0"} />
                        <FigmaTypography variant="label-2">.{type}</FigmaTypography>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                data-testid="presentation-export-cancel-button"
                onClick={() => setPresentationModalOpen(false)}
                disabled={isExporting}
              >
                {t("common:cancel")}
              </Button>
              <Button
                type="button"
                onClick={handlePresentationExport}
                disabled={isExporting || !presentationFileName.trim()}
                data-testid="presentation-export-submit"
              >
                {isExporting ? (
                  <>
                    <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                    {t("common:exporting")}
                  </>
                ) : (
                  t("common:export")
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        title={t("header.exportProject")}
        disabled={isDocumentExportDisabled}
        onClick={() => setModalOpen(true)}
        data-testid="export-menu-trigger"
      >
        <DownloadSimpleIcon className="size-4" />
        <FigmaTypography variant="label-2" color={isDocumentExportDisabled ? "zinc400" : "zinc700"}>
          {t("common:exportAs")}
        </FigmaTypography>
      </Button>

      <ExportOptionsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onExport={handleExport}
        isExporting={isExporting}
        projectName={selectedProject?.name || ""}
      />
    </>
  );
}
